import React, { useState, useEffect, useCallback } from "react";

// ── API ───────────────────────────────────────────────────────────────────────
const API = window.location.hostname === 'localhost' ? 'http://localhost:3002/api' : '/api';

async function apiFetch(path, options={}) {
  const token = localStorage.getItem('ger_token');
  const res = await fetch(API + path, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}), ...options.headers }
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.error || 'Error del servidor'); }
  return res.json();
}

const ROLES_LABEL = { ceo: "Gerente General", admin_general: "Coordinadora de Producción", contadora: "Contadora" };

// ── Módulos disponibles (se irán activando uno por uno) ───────────────────────
const MODULOS = [
  { id: "dashboard", icon: "⌂", label: "Inicio", activo: true },
  { id: "inventario", icon: "▣", label: "Inventario", activo: true },
  { id: "satelites", icon: "◎", label: "Satélites", activo: true },
];

// ── Utilidades de formato ──────────────────────────────────────────────────────
const formatCOP = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n) || 0);
const formatNum = (n) => new Intl.NumberFormat('es-CO').format(Number(n) || 0);
const formatFecha = (f) => {
  if (!f) return "—";
  const [y, m, d] = f.split('-');
  return `${d}/${m}/${y}`;
};
const hoyISO = () => new Date().toISOString().slice(0, 10);

// ── Paleta para gráficos (categórica, orden fijo) ──────────────────────────────
const COLORES_CALIBRE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];
const COLOR_ENTRADA = '#0ca30c';
const COLOR_SALIDA = '#d03b3b';

function App() {
  const [authed, setAuthed] = useState(false);
  const [loginForm, setLoginForm] = useState({ user: "", pass: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [page, setPage] = useState("dashboard");
  const [toast, setToast] = useState(null);
  const [showPerfil, setShowPerfil] = useState(false);

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3500); };

  const handleLogin = async () => {
    setLoginLoading(true);
    try {
      const data = await apiFetch('/login', { method:'POST', body:JSON.stringify({ usuario: loginForm.user, password: loginForm.pass }) });
      localStorage.setItem('ger_token', data.token);
      setCurrentUser(data.user);
      setAuthed(true);
      setPage("dashboard");
    } catch(e) {
      setLoginError(e.message || "Usuario o contraseña incorrectos");
      setTimeout(()=>setLoginError(""),3000);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setAuthed(false); setCurrentUser(null); setLoginForm({user:"",pass:""}); localStorage.removeItem('ger_token');
  };

  // ── LOGIN SCREEN ──
  if (!authed) {
    return (
      <div style={S.loginWrap}>
        <style>{CSS}</style>
        <div style={S.loginCard}>
          <div style={{textAlign:"center",marginBottom:"2rem"}}>
            <div style={{fontSize:32,fontWeight:200,letterSpacing:"0.15em",color:"#111"}}>ÁNGELA DURÁN</div>
            <div style={{fontSize:11,letterSpacing:"0.3em",color:"#999",marginTop:6,textTransform:"uppercase"}}>Sistema de Gerencia</div>
          </div>
          {loginError && <div style={S.loginError}>{loginError}</div>}
          <div style={{marginBottom:"1rem"}}>
            <label style={S.label}>Usuario</label>
            <input style={S.input} value={loginForm.user} onChange={e=>setLoginForm(f=>({...f,user:e.target.value}))} placeholder="Tu usuario" />
          </div>
          <div style={{marginBottom:"1.5rem"}}>
            <label style={S.label}>Contraseña</label>
            <input style={S.input} type="password" value={loginForm.pass} onChange={e=>setLoginForm(f=>({...f,pass:e.target.value}))} placeholder="Tu contraseña" onKeyDown={e=>e.key==="Enter"&&handleLogin()} />
          </div>
          <button style={S.btnPrimary} onClick={handleLogin} disabled={loginLoading}>{loginLoading?"Ingresando...":"Ingresar"}</button>
        </div>
      </div>
    );
  }

  // ── APP PRINCIPAL ──
  const iniciales = currentUser.nombre.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
  const nombreCorto = currentUser.nombre.split(" ").slice(0,2).join(" ");

  return (
    <div style={S.appWrap}>
      <style>{CSS}</style>
      {toast && <div style={{...S.toast, background: toast.type==="error"?"#D32F2F":"#2E7D52"}}>{toast.msg}</div>}

      {/* Barra superior */}
      <div style={S.topbar}>
        <div style={{display:"flex", alignItems:"center", gap:10, cursor:"pointer"}} onClick={()=>setPage("dashboard")}>
          <div style={{fontSize:17,fontWeight:300,letterSpacing:"0.1em",color:"#111"}}>ÁNGELA DURÁN</div>
          <div style={{fontSize:9,letterSpacing:"0.25em",color:"#999",textTransform:"uppercase",marginTop:2}}>Gerencia</div>
        </div>
        <div style={{display:"flex", alignItems:"center", gap:16}}>
          <div className="profile-block" style={{display:"flex", alignItems:"center", gap:10, cursor:"pointer"}} onClick={()=>setShowPerfil(true)}>
            <div style={S.avatar}>{iniciales}</div>
            <div>
              <div style={{fontSize:13,fontWeight:500,color:"#111"}}>{nombreCorto}</div>
              <div style={{fontSize:10,color:"#999"}}>{ROLES_LABEL[currentUser.rol]}</div>
            </div>
          </div>
          <button style={S.btnLogout} onClick={handleLogout}>Salir</button>
        </div>
      </div>

      {/* Contenido */}
      <div style={S.main}>
        {page==="dashboard" && (
          <>
            <div style={S.mainHeader}>
              <div>
                <div style={{fontSize:24,fontWeight:300,color:"#111"}}>Inicio</div>
                <div style={{fontSize:13,color:"#999"}}>Bienvenido, {nombreCorto}</div>
              </div>
            </div>
            <div style={S.mainContent}>
              <div className="grid2" style={S.grid2}>
                {MODULOS.filter(m=>m.id!=="dashboard" && m.activo).map(m=>(
                  <button key={m.id} className="insumo-card" style={S.insumoCard} onClick={()=>setPage(m.id)}>
                    <div style={S.insumoCardIcon}>{m.icon}</div>
                    <div style={S.insumoCardLabel}>{m.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {page==="inventario" && <InventarioPage currentUser={currentUser} showToast={showToast} onVolver={()=>setPage("dashboard")} />}
        {page==="satelites" && <SatelitesMaestro currentUser={currentUser} showToast={showToast} onVolver={()=>setPage("dashboard")} />}
      </div>

      {showPerfil && (
        <ModalPerfil currentUser={currentUser} iniciales={iniciales} onClose={()=>setShowPerfil(false)} showToast={showToast} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MÓDULO: INVENTARIO
// ═══════════════════════════════════════════════════════════════════════════

function InventarioPage({ currentUser, showToast, onVolver }) {
  const [insumoActivo, setInsumoActivo] = useState(null); // null = menú de insumos, "hilos" = submódulo abierto

  if (insumoActivo === "hilos") {
    return <HilosSubmodulo currentUser={currentUser} showToast={showToast} onVolver={()=>setInsumoActivo(null)} />;
  }

  return (
    <>
      <div style={S.mainHeader}>
        <div>
          <button style={{...S.btnOutline, marginBottom:10}} onClick={onVolver}>← Volver a Inicio</button>
          <div style={{fontSize:24,fontWeight:300,color:"#111"}}>Inventario</div>
          <div style={{fontSize:13,color:"#999"}}>Elige un tipo de insumo</div>
        </div>
      </div>
      <div style={S.mainContent}>
        <div className="grid2" style={S.grid2}>
          <button className="insumo-card" style={S.insumoCard} onClick={()=>setInsumoActivo("hilos")}>
            <div style={S.insumoCardIcon}>◍</div>
            <div style={S.insumoCardLabel}>Hilos</div>
            <div style={S.insumoCardSub}>Entradas, salidas y satélites</div>
          </button>
          <div style={S.insumoCardDisabled}>
            <div style={S.insumoCardIcon}>▥</div>
            <div style={{...S.insumoCardLabel, color:"#bbb"}}>Bolsas de empaque</div>
            <div style={S.insumoCardSub}>Próximamente</div>
          </div>
        </div>
      </div>
    </>
  );
}

function HilosSubmodulo({ currentUser, showToast, onVolver }) {
  const [tab, setTab] = useState("movimientos"); // "movimientos" | "satelites"

  return (
    <>
      <div style={S.mainHeader}>
        <div>
          <button style={{...S.btnOutline, marginBottom:10}} onClick={onVolver}>← Volver a Inventario</button>
          <div style={{fontSize:24,fontWeight:300,color:"#111"}}>Hilos</div>
          <div style={{fontSize:13,color:"#999"}}>Insumos ▸ Hilos</div>
        </div>
      </div>
      <div style={S.mainContent}>
        <div style={S.chipRow}>
          <div style={{...S.chip, ...(tab==="movimientos"?S.chipActive:{})}} onClick={()=>setTab("movimientos")}>Movimientos</div>
          <div style={{...S.chip, ...(tab==="satelites"?S.chipActive:{})}} onClick={()=>setTab("satelites")}>Satélites</div>
        </div>

        {tab === "movimientos" && <HilosMovimientos currentUser={currentUser} showToast={showToast} />}
        {tab === "satelites" && <HilosSatelites currentUser={currentUser} showToast={showToast} />}
      </div>
    </>
  );
}

function HilosMovimientos({ currentUser, showToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'entrada' | 'salida' | null
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const puedeGestionar = currentUser.rol !== 'contadora';

  const cargar = useCallback(async () => {
    try {
      const resumen = await apiFetch('/inventario/hilos/resumen');
      setData(resumen);
    } catch (e) {
      showToast(e.message || 'Error al cargar el inventario de hilos', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const cerrarModalYRecargar = async (mensaje) => {
    setModal(null);
    setLoading(true);
    await cargar();
    if (mensaje) showToast(mensaje);
  };

  const eliminarMovimiento = async () => {
    setDeleting(true);
    try {
      await apiFetch(`/inventario/hilos/movimientos/${confirmDeleteId}`, { method:'DELETE' });
      setConfirmDeleteId(null);
      setLoading(true);
      await cargar();
      showToast("Movimiento eliminado. El stock se recalculó automáticamente.");
    } catch (e) {
      showToast(e.message || "Error al eliminar el movimiento", "error");
    } finally {
      setDeleting(false);
    }
  };

  if (loading && !data) {
    return <div style={{padding:"3rem", textAlign:"center", color:"#999", fontSize:13}}>Cargando inventario de hilos...</div>;
  }
  if (!data) return null;

  const { calibres, movimientos, valorTotalInventario, totalHilos, valorPendienteCobro } = data;

  return (
    <>
      {/* Estadísticas clave */}
      <div style={S.statsRow}>
        <StatCard label="Hilos en stock" value={formatNum(totalHilos)} sub={`en ${calibres.length} calibres`} />
        <StatCard label="Valor del inventario" value={formatCOP(valorTotalInventario)} sub="a precio de compra actual" />
        <StatCard label="Por cobrar a satélites" value={formatCOP(valorPendienteCobro)} sub="salidas aún no cobradas" />
      </div>

      {/* Acciones */}
      {puedeGestionar && (
        <div style={{display:"flex", gap:12, marginBottom:"1.5rem"}}>
          <button style={S.btnPrimary2} onClick={()=>setModal('entrada')}>+ Registrar entrada</button>
          <button style={S.btnOutline} onClick={()=>setModal('salida')}>− Registrar salida</button>
        </div>
      )}
      {!puedeGestionar && (
        <div style={S.infoBanner}>Tu rol tiene acceso de solo lectura para registrar movimientos, pero puedes marcar salidas como cobradas.</div>
      )}

      {/* Gráfico de stock */}
      <div style={S.chartCard}>
        <div style={S.chartCardTitle}>Stock actual por calibre</div>
        <BarrasComparativas
          data={calibres.map((c, i) => ({ label: `Calibre ${c.nombre}`, value: Number(c.stock), color: COLORES_CALIBRE[i % COLORES_CALIBRE.length] }))}
        />
      </div>

      {/* Tarjetas por calibre */}
      <div className="grid2" style={{...S.grid2, marginTop:"1.5rem"}}>
        {calibres.map((c, i) => (
          <div key={c.id} style={S.calibreCard}>
            <div style={{display:"flex", alignItems:"center", gap:10}}>
              <div style={{width:10, height:10, borderRadius:"50%", background: COLORES_CALIBRE[i % COLORES_CALIBRE.length]}} />
              <div style={{fontSize:14, fontWeight:500, color:"#111"}}>Calibre {c.nombre}</div>
            </div>
            <div style={{fontSize:32, fontWeight:300, color:"#111", marginTop:10}}>{formatNum(c.stock)} <span style={{fontSize:13, color:"#999", fontWeight:400}}>hilos</span></div>
            <div style={{fontSize:12, color:"#999", marginTop:8, display:"flex", justifyContent:"space-between"}}>
              <span>Precio unitario</span><span style={{color:"#555"}}>{formatCOP(c.valor_unitario)}</span>
            </div>
            <div style={{fontSize:12, color:"#999", marginTop:4, display:"flex", justifyContent:"space-between"}}>
              <span>Valor en stock</span><span style={{color:"#555"}}>{formatCOP(c.stock * c.valor_unitario)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Historial de movimientos */}
      <div style={{marginTop:"1.5rem"}}>
        <div style={S.sectionTitle}>Movimientos recientes</div>
        <div style={S.tableCard}>
          {movimientos.length === 0 ? (
            <div style={{padding:"2.5rem", textAlign:"center", color:"#999", fontSize:13}}>Todavía no hay movimientos registrados.</div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Fecha</th>
                  <th style={S.th}>Tipo</th>
                  <th style={S.th}>Detalle</th>
                  <th style={S.th}>Proveedor / Satélite</th>
                  <th style={S.th}>Valor</th>
                  <th style={S.th}>Cobro</th>
                  <th style={S.th}>Registró</th>
                  {puedeGestionar && <th style={S.th}></th>}
                </tr>
              </thead>
              <tbody>
                {movimientos.map(m => (
                  <tr key={m.id}>
                    <td style={S.td}>{formatFecha(m.fecha)}</td>
                    <td style={S.td}><Badge tipo={m.tipo} /></td>
                    <td style={S.td}>{m.detalle.map(d=>`${d.calibre} × ${formatNum(d.cantidad)}`).join(", ")}</td>
                    <td style={S.td}>{m.tipo === 'salida' ? (m.satelite?.nombre || "—") : (m.proveedor || "—")}</td>
                    <td style={S.td}>{formatCOP(m.valor_total)}</td>
                    <td style={S.td}>
                      {m.tipo === 'salida' ? (
                        <span style={{...S.badge, background: m.cobrado ? "#E8F7EC" : "#FFF4E0", color: m.cobrado ? COLOR_ENTRADA : "#B77400"}}>
                          {m.cobrado ? "Cobrado" : "Pendiente"}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={S.td}>{m.usuario_nombre || "—"}</td>
                    {puedeGestionar && (
                      <td style={S.td}>
                        <button style={S.btnLinkDanger} onClick={()=>setConfirmDeleteId(m.id)}>Eliminar</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal === 'entrada' && (
        <ModalEntrada calibres={calibres} onClose={()=>setModal(null)} onSaved={()=>cerrarModalYRecargar("Entrada registrada correctamente")} showToast={showToast} />
      )}
      {modal === 'salida' && (
        <ModalSalida calibres={calibres} onClose={()=>setModal(null)} onSaved={()=>cerrarModalYRecargar("Salida registrada correctamente")} showToast={showToast} />
      )}
      {confirmDeleteId && (
        <ConfirmModal
          title="Eliminar movimiento"
          message="Esta acción no se puede deshacer. El stock y los saldos de los satélites se recalculan automáticamente al eliminar."
          confirmLabel={deleting ? "Eliminando..." : "Eliminar"}
          onCancel={()=>setConfirmDeleteId(null)}
          onConfirm={eliminarMovimiento}
          disabled={deleting}
        />
      )}
    </>
  );
}

function ModalEntrada({ calibres, onClose, onSaved, showToast }) {
  const [fecha, setFecha] = useState(hoyISO());
  const [proveedor, setProveedor] = useState("");
  const [observacion, setObservacion] = useState("");
  const [lineas, setLineas] = useState([{ calibre_id: calibres[0]?.id || "", cantidad: "" }]);
  const [saving, setSaving] = useState(false);

  const agregarLinea = () => {
    const usados = lineas.map(l => String(l.calibre_id));
    const disponible = calibres.find(c => !usados.includes(String(c.id)));
    setLineas(ls => [...ls, { calibre_id: disponible?.id || calibres[0]?.id || "", cantidad: "" }]);
  };
  const quitarLinea = (i) => setLineas(ls => ls.filter((_, idx) => idx !== i));
  const actualizarLinea = (i, campo, valor) => setLineas(ls => ls.map((l, idx) => idx === i ? { ...l, [campo]: valor } : l));

  const submit = async () => {
    const lineasValidas = lineas.filter(l => l.calibre_id && Number(l.cantidad) > 0);
    if (lineasValidas.length === 0) { showToast("Agrega al menos un calibre con cantidad", "error"); return; }
    setSaving(true);
    try {
      await apiFetch('/inventario/hilos/entradas', { method:'POST', body: JSON.stringify({ fecha, proveedor, observacion, lineas: lineasValidas }) });
      onSaved();
    } catch (e) {
      showToast(e.message || "Error al registrar la entrada", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Registrar entrada de hilos" onClose={onClose}>
      <div style={S.formRow}>
        <label style={S.formLabel}>Fecha</label>
        <input style={S.formInput} type="date" value={fecha} onChange={e=>setFecha(e.target.value)} />
      </div>
      <div style={S.formRow}>
        <label style={S.formLabel}>Proveedor (opcional)</label>
        <input style={S.formInput} value={proveedor} onChange={e=>setProveedor(e.target.value)} placeholder="¿A quién le compraste?" />
      </div>

      <label style={S.formLabel}>Calibres y cantidades</label>
      {lineas.map((l, i) => (
        <div key={i} style={S.lineaRow}>
          <select style={{...S.formInput, flex:1}} value={l.calibre_id} onChange={e=>actualizarLinea(i,'calibre_id',e.target.value)}>
            {calibres.map(c => <option key={c.id} value={c.id}>Calibre {c.nombre} — {formatCOP(c.valor_unitario)} c/u</option>)}
          </select>
          <input style={{...S.formInput, width:90}} type="number" min="1" value={l.cantidad} onChange={e=>actualizarLinea(i,'cantidad',e.target.value)} placeholder="Cant." />
          {lineas.length > 1 && <button type="button" style={S.btnRemoveLinea} onClick={()=>quitarLinea(i)}>✕</button>}
        </div>
      ))}
      {lineas.length < calibres.length && (
        <button type="button" style={S.btnAddLinea} onClick={agregarLinea}>+ Agregar otro calibre</button>
      )}

      <div style={{...S.formRow, marginTop:"1rem"}}>
        <label style={S.formLabel}>Observación (opcional)</label>
        <textarea style={{...S.formInput, minHeight:70, resize:"vertical"}} value={observacion} onChange={e=>setObservacion(e.target.value)} placeholder="Notas adicionales..." />
      </div>
      <button style={{...S.btnPrimary2, width:"100%", marginTop:"0.5rem"}} onClick={submit} disabled={saving}>{saving?"Guardando...":"Guardar entrada"}</button>
    </Modal>
  );
}

function ModalSalida({ calibres, onClose, onSaved, showToast }) {
  const [fecha, setFecha] = useState(hoyISO());
  const [sateliteId, setSateliteId] = useState("");
  const [satelites, setSatelites] = useState(null);
  const [observacion, setObservacion] = useState("");
  const [lineas, setLineas] = useState([{ calibre_id: calibres[0]?.id || "", cantidad: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch('/satelites').then(data => {
      const activos = data.filter(s => s.estado === 'activo');
      setSatelites(activos);
      if (activos.length > 0) setSateliteId(activos[0].id);
    }).catch(() => setSatelites([]));
  }, []);

  const agregarLinea = () => {
    const usados = lineas.map(l => String(l.calibre_id));
    const disponible = calibres.find(c => !usados.includes(String(c.id)));
    setLineas(ls => [...ls, { calibre_id: disponible?.id || calibres[0]?.id || "", cantidad: "" }]);
  };
  const quitarLinea = (i) => setLineas(ls => ls.filter((_, idx) => idx !== i));
  const actualizarLinea = (i, campo, valor) => setLineas(ls => ls.map((l, idx) => idx === i ? { ...l, [campo]: valor } : l));

  const stockDe = (calibreId) => calibres.find(c => String(c.id) === String(calibreId))?.stock ?? 0;

  const submit = async () => {
    if (!sateliteId) { showToast("Selecciona el satélite que retira los hilos", "error"); return; }
    const lineasValidas = lineas.filter(l => l.calibre_id && Number(l.cantidad) > 0);
    if (lineasValidas.length === 0) { showToast("Agrega al menos un calibre con cantidad", "error"); return; }
    for (const l of lineasValidas) {
      if (Number(l.cantidad) > Number(stockDe(l.calibre_id))) {
        showToast(`Solo hay ${stockDe(l.calibre_id)} hilos disponibles de ese calibre`, "error");
        return;
      }
    }
    setSaving(true);
    try {
      await apiFetch('/inventario/hilos/salidas', { method:'POST', body: JSON.stringify({ fecha, satelite_id: sateliteId, observacion, lineas: lineasValidas }) });
      onSaved();
    } catch (e) {
      showToast(e.message || "Error al registrar la salida", "error");
    } finally {
      setSaving(false);
    }
  };

  if (satelites && satelites.length === 0) {
    return (
      <Modal title="Registrar salida de hilos" onClose={onClose}>
        <div style={{fontSize:13, color:"#777", marginBottom:16}}>
          Todavía no tienes satélites registrados. Ve a la pestaña "Satélites" (dentro de Hilos) y crea uno primero para poder registrar salidas.
        </div>
        <button style={{...S.btnOutline, width:"100%"}} onClick={onClose}>Entendido</button>
      </Modal>
    );
  }

  return (
    <Modal title="Registrar salida de hilos" onClose={onClose}>
      <div style={S.formRow}>
        <label style={S.formLabel}>Satélite (¿quién retira?)</label>
        <select style={S.formInput} value={sateliteId} onChange={e=>setSateliteId(e.target.value)} disabled={!satelites}>
          {!satelites && <option>Cargando...</option>}
          {satelites && satelites.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
        </select>
      </div>
      <div style={S.formRow}>
        <label style={S.formLabel}>Fecha</label>
        <input style={S.formInput} type="date" value={fecha} onChange={e=>setFecha(e.target.value)} />
      </div>

      <label style={S.formLabel}>Calibres y cantidades</label>
      {lineas.map((l, i) => (
        <div key={i} style={S.lineaRow}>
          <select style={{...S.formInput, flex:1}} value={l.calibre_id} onChange={e=>actualizarLinea(i,'calibre_id',e.target.value)}>
            {calibres.map(c => <option key={c.id} value={c.id}>Calibre {c.nombre} — {formatNum(c.stock)} disp.</option>)}
          </select>
          <input style={{...S.formInput, width:90}} type="number" min="1" value={l.cantidad} onChange={e=>actualizarLinea(i,'cantidad',e.target.value)} placeholder="Cant." />
          {lineas.length > 1 && <button type="button" style={S.btnRemoveLinea} onClick={()=>quitarLinea(i)}>✕</button>}
        </div>
      ))}
      {lineas.length < calibres.length && (
        <button type="button" style={S.btnAddLinea} onClick={agregarLinea}>+ Agregar otro calibre</button>
      )}

      <div style={{...S.formRow, marginTop:"1rem"}}>
        <label style={S.formLabel}>Observación (opcional)</label>
        <textarea style={{...S.formInput, minHeight:70, resize:"vertical"}} value={observacion} onChange={e=>setObservacion(e.target.value)} placeholder="Ej: corte de vestidos para..." />
      </div>
      <button style={{...S.btnDanger, width:"100%", marginTop:"0.5rem"}} onClick={submit} disabled={saving}>{saving?"Guardando...":"Guardar salida"}</button>
    </Modal>
  );
}

// ─── Satélites, vista anidada dentro de Hilos (filtrada solo a esa actividad) ─

function HilosSatelites({ currentUser, showToast }) {
  const [satelites, setSatelites] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seleccionado, setSeleccionado] = useState(null);

  const puedeGestionar = currentUser.rol !== 'contadora';

  const cargar = useCallback(async () => {
    try {
      const data = await apiFetch('/satelites');
      setSatelites(data);
    } catch (e) {
      showToast(e.message || 'Error al cargar satélites', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (seleccionado) {
    return <SateliteDetalle id={seleccionado} onVolver={()=>{ setSeleccionado(null); cargar(); }} showToast={showToast} puedeGestionar={puedeGestionar} />;
  }

  return (
    <>
      <div style={{marginBottom:"1rem"}}>
        <div style={S.sectionTitle}>Satélites — actividad de hilos</div>
      </div>

      {loading ? (
        <div style={{padding:"3rem", textAlign:"center", color:"#999", fontSize:13}}>Cargando satélites...</div>
      ) : satelites.length === 0 ? (
        <div style={S.emptyState}>
          <div style={{fontSize:48,marginBottom:16,opacity:0.3}}>◎</div>
          <div style={{fontSize:16,color:"#555",marginBottom:8}}>Todavía no tienes satélites registrados</div>
          <div style={{fontSize:13,color:"#999",maxWidth:400,textAlign:"center"}}>
            Crea el primer satélite desde Satélites, en el menú principal, y aquí verás su actividad de hilos.
          </div>
        </div>
      ) : (
        <div style={S.tableCard}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Nombre</th>
                <th style={S.th}>Teléfono</th>
                <th style={S.th}>Hilos entregados</th>
                <th style={S.th}>Valor entregado</th>
                <th style={S.th}>Pendiente de cobro</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {satelites.map(s => (
                <tr key={s.id} style={{cursor:"pointer"}} onClick={()=>setSeleccionado(s.id)}>
                  <td style={S.td}>{s.nombre}{s.estado !== 'activo' && <span style={{color:"#bbb",fontSize:11}}> (inactivo)</span>}</td>
                  <td style={S.td}>{s.telefono || "—"}</td>
                  <td style={S.td}>{formatNum(s.total_hilos)}</td>
                  <td style={S.td}>{formatCOP(s.valor_total_entregado)}</td>
                  <td style={S.td}>
                    <span style={{color: Number(s.valor_pendiente) > 0 ? COLOR_SALIDA : "#999", fontWeight: Number(s.valor_pendiente) > 0 ? 600 : 400}}>
                      {formatCOP(s.valor_pendiente)}
                    </span>
                  </td>
                  <td style={S.td}>›</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function SateliteDetalle({ id, onVolver, showToast, puedeGestionar }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const d = await apiFetch(`/satelites/${id}`);
      setData(d);
    } catch (e) {
      showToast(e.message || 'Error al cargar el satélite', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const toggleCobro = async (movId, actual) => {
    try {
      await apiFetch(`/inventario/hilos/movimientos/${movId}/cobro`, { method:'PATCH', body: JSON.stringify({ cobrado: !actual }) });
      await cargar();
      showToast(!actual ? "Marcado como cobrado" : "Marcado como pendiente");
    } catch (e) {
      showToast(e.message || "Error al actualizar", "error");
    }
  };

  const eliminarMovimiento = async () => {
    setDeleting(true);
    try {
      await apiFetch(`/inventario/hilos/movimientos/${confirmDeleteId}`, { method:'DELETE' });
      setConfirmDeleteId(null);
      await cargar();
      showToast("Movimiento eliminado. El saldo se recalculó automáticamente.");
    } catch (e) {
      showToast(e.message || "Error al eliminar el movimiento", "error");
    } finally {
      setDeleting(false);
    }
  };

  if (loading || !data) {
    return (
      <>
        <button style={{...S.btnOutline, marginBottom:16}} onClick={onVolver}>← Volver a Satélites</button>
        <div style={{padding:"3rem", textAlign:"center", color:"#999", fontSize:13}}>Cargando...</div>
      </>
    );
  }

  const { satelite, movimientos, valorTotalEntregado, valorPendiente, totalHilos } = data;

  return (
    <>
      <button style={{...S.btnOutline, marginBottom:16}} onClick={onVolver}>← Volver a Satélites</button>

      <div style={{marginBottom:20}}>
        <div style={{fontSize:20, fontWeight:500, color:"#111"}}>{satelite.nombre}</div>
        <div style={{fontSize:13,color:"#999", marginTop:2}}>{satelite.telefono || "Sin teléfono"}{satelite.direccion ? ` · ${satelite.direccion}` : ""}</div>
        {satelite.contacto && <div style={{fontSize:13, color:"#777", marginTop:6}}>Contacto: {satelite.contacto}</div>}
        {satelite.observaciones && <div style={{fontSize:13, color:"#777", marginTop:4}}>{satelite.observaciones}</div>}
      </div>

      <div style={S.statsRow}>
        <StatCard label="Hilos entregados" value={formatNum(totalHilos)} />
        <StatCard label="Valor total entregado" value={formatCOP(valorTotalEntregado)} />
        <StatCard label="Pendiente de cobro" value={formatCOP(valorPendiente)} />
      </div>

      <div style={S.sectionTitle}>Historial de entregas (hilos)</div>
      <div style={S.tableCard}>
        {movimientos.length === 0 ? (
          <div style={{padding:"2.5rem", textAlign:"center", color:"#999", fontSize:13}}>Este satélite todavía no tiene salidas de hilos registradas.</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Fecha</th>
                <th style={S.th}>Detalle</th>
                <th style={S.th}>Valor</th>
                <th style={S.th}>Estado</th>
                <th style={S.th}></th>
                {puedeGestionar && <th style={S.th}></th>}
              </tr>
            </thead>
            <tbody>
              {movimientos.map(m => (
                <tr key={m.id}>
                  <td style={S.td}>{formatFecha(m.fecha)}</td>
                  <td style={S.td}>{m.detalle.map(d=>`${d.calibre} × ${formatNum(d.cantidad)}`).join(", ")}</td>
                  <td style={S.td}>{formatCOP(m.valor_total)}</td>
                  <td style={S.td}>
                    <span style={{...S.badge, background: m.cobrado ? "#E8F7EC" : "#FFF4E0", color: m.cobrado ? COLOR_ENTRADA : "#B77400"}}>
                      {m.cobrado ? `✓ Cobrado (${formatFecha(m.fecha_cobro)})` : "Pendiente"}
                    </span>
                  </td>
                  <td style={S.td}>
                    <button style={S.btnLink} onClick={()=>toggleCobro(m.id, m.cobrado)}>
                      {m.cobrado ? "Marcar pendiente" : "Marcar cobrado"}
                    </button>
                  </td>
                  {puedeGestionar && (
                    <td style={S.td}>
                      <button style={S.btnLinkDanger} onClick={()=>setConfirmDeleteId(m.id)}>Eliminar</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirmDeleteId && (
        <ConfirmModal
          title="Eliminar movimiento"
          message="Esta acción no se puede deshacer. El stock de hilos y el saldo de este satélite se recalculan automáticamente al eliminar."
          confirmLabel={deleting ? "Eliminando..." : "Eliminar"}
          onCancel={()=>setConfirmDeleteId(null)}
          onConfirm={eliminarMovimiento}
          disabled={deleting}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MÓDULO: SATÉLITES (directorio maestro — vista consolidada, no solo de hilos)
// ═══════════════════════════════════════════════════════════════════════════

function SatelitesMaestro({ currentUser, showToast, onVolver }) {
  const [satelites, setSatelites] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seleccionado, setSeleccionado] = useState(null);
  const [showCrear, setShowCrear] = useState(false);

  const puedeGestionar = currentUser.rol !== 'contadora';

  const cargar = useCallback(async () => {
    try {
      const data = await apiFetch('/satelites');
      setSatelites(data);
    } catch (e) {
      showToast(e.message || 'Error al cargar satélites', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (seleccionado) {
    return <SateliteMaestroDetalle id={seleccionado} onVolver={()=>{ setSeleccionado(null); cargar(); }} showToast={showToast} puedeGestionar={puedeGestionar} />;
  }

  return (
    <>
      <div style={S.mainHeader}>
        <div>
          <button style={{...S.btnOutline, marginBottom:10}} onClick={onVolver}>← Volver a Inicio</button>
          <div style={{fontSize:24,fontWeight:300,color:"#111"}}>Satélites</div>
          <div style={{fontSize:13,color:"#999"}}>Directorio maestro — información consolidada de todos los módulos</div>
        </div>
        {puedeGestionar && <button style={S.btnPrimary2} onClick={()=>setShowCrear(true)}>+ Nuevo satélite</button>}
      </div>
      <div style={S.mainContent}>
        {loading ? (
          <div style={{padding:"3rem", textAlign:"center", color:"#999", fontSize:13}}>Cargando satélites...</div>
        ) : satelites.length === 0 ? (
          <div style={S.emptyState}>
            <div style={{fontSize:48,marginBottom:16,opacity:0.3}}>◎</div>
            <div style={{fontSize:16,color:"#555",marginBottom:8}}>Todavía no tienes satélites registrados</div>
            <div style={{fontSize:13,color:"#999",maxWidth:400,textAlign:"center", marginBottom:16}}>
              Crea el primer satélite (taller o persona) para empezar a llevar el control, tanto en Hilos como en los módulos que se vayan agregando.
            </div>
            {puedeGestionar && <button style={S.btnPrimary2} onClick={()=>setShowCrear(true)}>+ Nuevo satélite</button>}
          </div>
        ) : (
          <div style={S.tableCard}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Nombre</th>
                  <th style={S.th}>Teléfono</th>
                  <th style={S.th}>Contacto</th>
                  <th style={S.th}>Valor entregado (todos los módulos)</th>
                  <th style={S.th}>Pendiente de cobro</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {satelites.map(s => (
                  <tr key={s.id} style={{cursor:"pointer"}} onClick={()=>setSeleccionado(s.id)}>
                    <td style={S.td}>{s.nombre}{s.estado !== 'activo' && <span style={{color:"#bbb",fontSize:11}}> (inactivo)</span>}</td>
                    <td style={S.td}>{s.telefono || "—"}</td>
                    <td style={S.td}>{s.contacto || "—"}</td>
                    <td style={S.td}>{formatCOP(s.valor_total_entregado)}</td>
                    <td style={S.td}>
                      <span style={{color: Number(s.valor_pendiente) > 0 ? COLOR_SALIDA : "#999", fontWeight: Number(s.valor_pendiente) > 0 ? 600 : 400}}>
                        {formatCOP(s.valor_pendiente)}
                      </span>
                    </td>
                    <td style={S.td}>›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCrear && (
        <ModalSatelite
          onClose={()=>setShowCrear(false)}
          onSaved={async ()=>{ setShowCrear(false); setLoading(true); await cargar(); showToast('Satélite creado correctamente'); }}
          showToast={showToast}
        />
      )}
    </>
  );
}

function SateliteMaestroDetalle({ id, onVolver, showToast, puedeGestionar }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditar, setShowEditar] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const d = await apiFetch(`/satelites/${id}`);
      setData(d);
    } catch (e) {
      showToast(e.message || 'Error al cargar el satélite', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading || !data) {
    return (
      <div style={S.mainContent}>
        <button style={{...S.btnOutline, marginBottom:16}} onClick={onVolver}>← Volver a Satélites</button>
        <div style={{padding:"3rem", textAlign:"center", color:"#999", fontSize:13}}>Cargando...</div>
      </div>
    );
  }

  const { satelite, movimientos, valorTotalEntregado, valorPendiente, totalHilos } = data;

  return (
    <div style={S.mainContent}>
      <button style={{...S.btnOutline, marginBottom:16}} onClick={onVolver}>← Volver a Satélites</button>

      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20}}>
        <div>
          <div style={{fontSize:20, fontWeight:500, color:"#111"}}>{satelite.nombre}</div>
          <div style={{fontSize:13,color:"#999", marginTop:2}}>{satelite.telefono || "Sin teléfono"}{satelite.direccion ? ` · ${satelite.direccion}` : ""}</div>
          {satelite.contacto && <div style={{fontSize:13, color:"#777", marginTop:6}}>Contacto: {satelite.contacto}</div>}
          {satelite.observaciones && <div style={{fontSize:13, color:"#777", marginTop:4}}>{satelite.observaciones}</div>}
        </div>
        {puedeGestionar && <button style={S.btnOutline} onClick={()=>setShowEditar(true)}>Editar datos</button>}
      </div>

      <div style={S.sectionTitle}>Resumen consolidado (todos los módulos)</div>
      <div style={S.statsRow}>
        <StatCard label="Total entregado" value={formatCOP(valorTotalEntregado)} />
        <StatCard label="Pendiente de cobro" value={formatCOP(valorPendiente)} />
        <StatCard label="Hilos entregados" value={formatNum(totalHilos)} />
      </div>

      <div style={{marginTop:"1.5rem"}}>
        <div style={S.sectionTitle}>▸ Hilos</div>
        <div style={S.tableCard}>
          {movimientos.length === 0 ? (
            <div style={{padding:"2.5rem", textAlign:"center", color:"#999", fontSize:13}}>Este satélite todavía no tiene actividad de hilos.</div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Fecha</th>
                  <th style={S.th}>Detalle</th>
                  <th style={S.th}>Valor</th>
                  <th style={S.th}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {movimientos.map(m => (
                  <tr key={m.id}>
                    <td style={S.td}>{formatFecha(m.fecha)}</td>
                    <td style={S.td}>{m.detalle.map(d=>`${d.calibre} × ${formatNum(d.cantidad)}`).join(", ")}</td>
                    <td style={S.td}>{formatCOP(m.valor_total)}</td>
                    <td style={S.td}>
                      <span style={{...S.badge, background: m.cobrado ? "#E8F7EC" : "#FFF4E0", color: m.cobrado ? COLOR_ENTRADA : "#B77400"}}>
                        {m.cobrado ? "Cobrado" : "Pendiente"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{fontSize:11, color:"#bbb", marginTop:8}}>Para registrar, cobrar o eliminar movimientos de hilos, ve a Inventario ▸ Hilos ▸ Satélites.</div>
      </div>

      <div style={{marginTop:"1.5rem", padding:"1.5rem", border:"1px dashed #ddd", borderRadius:12, textAlign:"center", color:"#bbb", fontSize:13}}>
        Aquí se irán agregando los demás módulos (Cortes, etc.) a medida que estén activos.
      </div>

      {showEditar && (
        <ModalSatelite
          satelite={satelite}
          onClose={()=>setShowEditar(false)}
          onSaved={async ()=>{ setShowEditar(false); setLoading(true); await cargar(); showToast('Satélite actualizado correctamente'); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

function ModalSatelite({ satelite, onClose, onSaved, showToast }) {
  const [form, setForm] = useState({
    nombre: satelite?.nombre || "",
    telefono: satelite?.telefono || "",
    direccion: satelite?.direccion || "",
    contacto: satelite?.contacto || "",
    observaciones: satelite?.observaciones || "",
    estado: satelite?.estado || "activo",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.nombre.trim()) { showToast("El nombre es obligatorio", "error"); return; }
    setSaving(true);
    try {
      if (satelite) {
        await apiFetch(`/satelites/${satelite.id}`, { method:'PUT', body: JSON.stringify(form) });
      } else {
        await apiFetch('/satelites', { method:'POST', body: JSON.stringify(form) });
      }
      onSaved();
    } catch (e) {
      showToast(e.message || "Error al guardar el satélite", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={satelite ? "Editar satélite" : "Nuevo satélite"} onClose={onClose}>
      <div style={S.formRow}>
        <label style={S.formLabel}>Nombre</label>
        <input style={S.formInput} value={form.nombre} onChange={e=>setForm(f=>({...f, nombre:e.target.value}))} placeholder="Ej: Taller Sra. Rocío" />
      </div>
      <div style={S.formRow}>
        <label style={S.formLabel}>Teléfono</label>
        <input style={S.formInput} value={form.telefono} onChange={e=>setForm(f=>({...f, telefono:e.target.value}))} placeholder="Ej: 300 123 4567" />
      </div>
      <div style={S.formRow}>
        <label style={S.formLabel}>Dirección</label>
        <input style={S.formInput} value={form.direccion} onChange={e=>setForm(f=>({...f, direccion:e.target.value}))} placeholder="Opcional" />
      </div>
      <div style={S.formRow}>
        <label style={S.formLabel}>Persona de contacto</label>
        <input style={S.formInput} value={form.contacto} onChange={e=>setForm(f=>({...f, contacto:e.target.value}))} placeholder="Opcional" />
      </div>
      <div style={S.formRow}>
        <label style={S.formLabel}>Observaciones</label>
        <textarea style={{...S.formInput, minHeight:70, resize:"vertical"}} value={form.observaciones} onChange={e=>setForm(f=>({...f, observaciones:e.target.value}))} placeholder="Opcional" />
      </div>
      {satelite && (
        <div style={S.formRow}>
          <label style={S.formLabel}>Estado</label>
          <select style={S.formInput} value={form.estado} onChange={e=>setForm(f=>({...f, estado:e.target.value}))}>
            <option value="activo">Activo</option>
            <option value="inactivo">Inactivo</option>
          </select>
        </div>
      )}
      <button style={{...S.btnPrimary2, width:"100%", marginTop:"0.5rem"}} onClick={submit} disabled={saving}>{saving?"Guardando...":(satelite ? "Guardar cambios" : "Crear satélite")}</button>
    </Modal>
  );
}

// ── Perfil y cambio de contraseña ───────────────────────────────────────────

function ModalPerfil({ currentUser, iniciales, onClose, showToast }) {
  const [modo, setModo] = useState('perfil'); // 'perfil' | 'password'

  if (modo === 'password') {
    return <ModalCambiarPassword onClose={onClose} onVolver={()=>setModo('perfil')} showToast={showToast} />;
  }

  return (
    <Modal title="Mi perfil" onClose={onClose}>
      <div style={{display:"flex", alignItems:"center", gap:14, marginBottom:"1.5rem"}}>
        <div style={{...S.avatar, width:52, height:52, fontSize:18}}>{iniciales}</div>
        <div>
          <div style={{fontSize:16, fontWeight:500, color:"#111"}}>{currentUser.nombre}</div>
          <div style={{fontSize:12, color:"#999"}}>{ROLES_LABEL[currentUser.rol]}</div>
        </div>
      </div>
      <button style={{...S.btnOutline, width:"100%"}} onClick={()=>setModo('password')}>Cambiar contraseña</button>
    </Modal>
  );
}

function ModalCambiarPassword({ onClose, onVolver, showToast }) {
  const [nueva, setNueva] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!nueva || nueva.length < 4) { showToast("La contraseña debe tener al menos 4 caracteres", "error"); return; }
    if (nueva !== confirmar) { showToast("Las contraseñas no coinciden", "error"); return; }
    setSaving(true);
    try {
      await apiFetch('/change-password', { method:'POST', body: JSON.stringify({ newPassword: nueva }) });
      showToast("Contraseña actualizada correctamente");
      onClose();
    } catch (e) {
      showToast(e.message || "Error al cambiar la contraseña", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Cambiar contraseña" onClose={onClose}>
      {onVolver && <button style={{...S.btnLink, marginBottom:"1rem"}} onClick={onVolver}>← Volver a mi perfil</button>}
      <div style={S.formRow}>
        <label style={S.formLabel}>Nueva contraseña</label>
        <input style={S.formInput} type="password" value={nueva} onChange={e=>setNueva(e.target.value)} placeholder="Mínimo 4 caracteres" onKeyDown={e=>e.key==="Enter"&&submit()} />
      </div>
      <div style={S.formRow}>
        <label style={S.formLabel}>Confirmar contraseña</label>
        <input style={S.formInput} type="password" value={confirmar} onChange={e=>setConfirmar(e.target.value)} placeholder="Repite la contraseña" onKeyDown={e=>e.key==="Enter"&&submit()} />
      </div>
      <button style={{...S.btnPrimary2, width:"100%", marginTop:"0.5rem"}} onClick={submit} disabled={saving}>{saving?"Guardando...":"Actualizar contraseña"}</button>
    </Modal>
  );
}

// ── Componentes reutilizables ───────────────────────────────────────────────

function StatCard({ label, value, sub }) {
  return (
    <div style={S.statCard}>
      <div style={S.statCardLabel}>{label}</div>
      <div style={S.statCardValue}>{value}</div>
      {sub && <div style={S.statCardSub}>{sub}</div>}
    </div>
  );
}

function Badge({ tipo }) {
  const esEntrada = tipo === 'entrada';
  return (
    <span style={{...S.badge, background: esEntrada ? "#E8F7EC" : "#FBEAEA", color: esEntrada ? COLOR_ENTRADA : COLOR_SALIDA}}>
      {esEntrada ? "▲ Entrada" : "▼ Salida"}
    </span>
  );
}

function BarrasComparativas({ data, height=150 }) {
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const barMax = height - 46;
  return (
    <div style={{display:"flex", alignItems:"flex-end", gap:28, height, padding:"0 8px"}}>
      {data.map((d, i) => {
        const h = Math.max(Math.round((d.value / maxVal) * barMax), 3);
        return (
          <div key={i} style={{display:"flex", flexDirection:"column", alignItems:"center", flex:1, height:"100%", justifyContent:"flex-end"}}>
            <div style={{fontSize:13, fontWeight:600, color:"#111", marginBottom:6}}>{formatNum(d.value)}</div>
            <div style={{width:"100%", maxWidth:56, height:h, background:d.color, borderRadius:"4px 4px 2px 2px"}} />
            <div style={{fontSize:11, color:"#999", marginTop:8, textAlign:"center"}}>{d.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalCard} onClick={e=>e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div style={{fontSize:16, fontWeight:500, color:"#111"}}>{title}</div>
          <button style={S.modalClose} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"1.5rem"}}>{children}</div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, onConfirm, onCancel, confirmLabel="Eliminar", disabled }) {
  return (
    <Modal title={title} onClose={onCancel}>
      <div style={{fontSize:13, color:"#555", marginBottom:"1.5rem"}}>{message}</div>
      <div style={{display:"flex", gap:10}}>
        <button style={{...S.btnOutline, flex:1}} onClick={onCancel} disabled={disabled}>Cancelar</button>
        <button style={{...S.btnDanger, flex:1}} onClick={onConfirm} disabled={disabled}>{confirmLabel}</button>
      </div>
    </Modal>
  );
}

// ── Estilos ───────────────────────────────────────────────────────────────────
const S = {
  loginWrap: { minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#f5f5f5", fontFamily:"'Century Gothic', sans-serif" },
  loginCard: { background:"#fff", borderRadius:16, padding:"3rem", width:"100%", maxWidth:400, boxShadow:"0 4px 24px rgba(0,0,0,0.08)" },
  loginError: { background:"#FFEBEE", color:"#C62828", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:"1rem", textAlign:"center" },
  label: { display:"block", fontSize:10, letterSpacing:"0.15em", textTransform:"uppercase", color:"#999", marginBottom:6 },
  input: { width:"100%", padding:"12px 14px", border:"1px solid #ddd", borderRadius:8, fontSize:14, boxSizing:"border-box", fontFamily:"inherit" },
  btnPrimary: { width:"100%", padding:"13px", background:"#111", color:"#fff", border:"none", borderRadius:8, fontSize:13, letterSpacing:"0.1em", textTransform:"uppercase", cursor:"pointer", fontFamily:"inherit" },

  appWrap: { minHeight:"100vh", background:"#fafafa", fontFamily:"'Century Gothic', sans-serif" },
  topbar: { position:"fixed", top:0, left:0, right:0, height:64, background:"#fff", borderBottom:"1px solid #eee", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 2rem", zIndex:100 },
  avatar: { width:36, height:36, borderRadius:"50%", background:"#111", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:500 },
  btnLogout: { background:"none", border:"none", color:"#999", fontSize:12, cursor:"pointer", fontFamily:"inherit" },
  main: { marginTop:64 },
  mainHeader: { padding:"2rem 2.5rem", borderBottom:"1px solid #eee", background:"#fff", display:"flex", justifyContent:"space-between", alignItems:"center" },
  mainContent: { padding:"2.5rem" },
  emptyState: { display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"4rem 2rem", background:"#fff", borderRadius:12, border:"1px solid #eee" },
  toast: { position:"fixed", top:20, right:20, color:"#fff", padding:"12px 20px", borderRadius:8, fontSize:13, zIndex:1000, boxShadow:"0 4px 16px rgba(0,0,0,0.15)" },

  // Inicio / Inventario — selector de módulos e insumos (mismo look de tarjeta-botón)
  insumoCard: { background:"#fff", border:"1px solid #eee", borderRadius:12, padding:"2rem", cursor:"pointer", textAlign:"left", fontFamily:"inherit", display:"flex", flexDirection:"column", gap:8, transition:"all 0.15s" },
  insumoCardDisabled: { background:"#fafafa", border:"1px dashed #ddd", borderRadius:12, padding:"2rem", display:"flex", flexDirection:"column", gap:8 },
  insumoCardIcon: { fontSize:30, color:"#999" },
  insumoCardLabel: { fontSize:16, fontWeight:500, color:"#111" },
  insumoCardSub: { fontSize:12, color:"#999" },

  // Pestañas (tabs) dentro de un submódulo
  chipRow: { display:"flex", gap:10, marginBottom:"1.5rem", alignItems:"center" },
  chip: { padding:"7px 16px", borderRadius:20, fontSize:13, fontWeight:500, background:"#fff", border:"1px solid #eee", color:"#555", cursor:"pointer" },
  chipActive: { background:"#111", color:"#fff", border:"1px solid #111" },
  chipDisabled: { padding:"7px 16px", borderRadius:20, fontSize:12, color:"#bbb", fontStyle:"italic" },

  statsRow: { display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:16, marginBottom:"1.5rem" },
  statCard: { background:"#fff", border:"1px solid #eee", borderRadius:12, padding:"1.5rem" },
  statCardLabel: { fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", color:"#999", marginBottom:10 },
  statCardValue: { fontSize:26, fontWeight:300, color:"#111" },
  statCardSub: { fontSize:12, color:"#bbb", marginTop:6 },

  btnPrimary2: { padding:"11px 20px", background:"#111", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", fontFamily:"inherit" },
  btnOutline: { padding:"11px 20px", background:"#fff", color:"#111", border:"1px solid #ddd", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", fontFamily:"inherit" },
  btnDanger: { padding:"11px 20px", background:"#d03b3b", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", fontFamily:"inherit" },
  btnLink: { background:"none", border:"none", color:"#111", fontSize:12, textDecoration:"underline", cursor:"pointer", fontFamily:"inherit" },
  btnLinkDanger: { background:"none", border:"none", color:"#d03b3b", fontSize:12, textDecoration:"underline", cursor:"pointer", fontFamily:"inherit" },
  infoBanner: { background:"#F5F5F5", color:"#777", fontSize:13, padding:"10px 16px", borderRadius:8, marginBottom:"1.5rem" },

  grid2: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 },
  chartCard: { background:"#fff", border:"1px solid #eee", borderRadius:12, padding:"1.5rem" },
  chartCardTitle: { fontSize:13, fontWeight:500, color:"#555", marginBottom:"1rem" },
  calibreCard: { background:"#fff", border:"1px solid #eee", borderRadius:12, padding:"1.5rem" },

  sectionTitle: { fontSize:15, fontWeight:500, color:"#111", marginBottom:"0.75rem" },
  tableCard: { background:"#fff", border:"1px solid #eee", borderRadius:12, overflow:"hidden" },
  table: { width:"100%", borderCollapse:"collapse", fontSize:13 },
  th: { textAlign:"left", padding:"12px 16px", fontSize:10, letterSpacing:"0.08em", textTransform:"uppercase", color:"#999", borderBottom:"1px solid #eee", background:"#fafafa" },
  td: { padding:"12px 16px", borderBottom:"1px solid #f3f3f3", color:"#333" },
  badge: { display:"inline-flex", alignItems:"center", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:600, letterSpacing:"0.02em" },

  modalOverlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:2000 },
  modalCard: { background:"#fff", borderRadius:14, width:"100%", maxWidth:440, maxHeight:"88vh", overflowY:"auto", boxShadow:"0 8px 32px rgba(0,0,0,0.2)" },
  modalHeader: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"1.25rem 1.5rem", borderBottom:"1px solid #eee" },
  modalClose: { background:"none", border:"none", fontSize:16, color:"#999", cursor:"pointer" },
  formRow: { marginBottom:"1rem" },
  formLabel: { display:"block", fontSize:10, letterSpacing:"0.1em", textTransform:"uppercase", color:"#999", marginBottom:6 },
  formInput: { width:"100%", padding:"11px 13px", border:"1px solid #ddd", borderRadius:8, fontSize:14, boxSizing:"border-box", fontFamily:"inherit" },
  lineaRow: { display:"flex", gap:8, alignItems:"center", marginBottom:8 },
  btnAddLinea: { background:"none", border:"1px dashed #ccc", borderRadius:8, padding:"8px 14px", fontSize:12, color:"#555", cursor:"pointer", fontFamily:"inherit", marginBottom:8 },
  btnRemoveLinea: { background:"#FBEAEA", border:"none", color:"#d03b3b", borderRadius:6, width:32, height:38, cursor:"pointer", fontSize:13, flexShrink:0 },
};

const CSS = `
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family:'Century Gothic', sans-serif; }
button:hover { opacity:0.85; }
input:focus, select:focus, textarea:focus { outline:none; border-color:#111; }
.insumo-card:hover { border-color:#111; }
.profile-block:hover { opacity:0.75; }
@media (max-width: 900px) {
  .grid2 { grid-template-columns: 1fr !important; }
}
`;

export default App;
